/**
 * Transient generation state for AI-embedded canvases, keyed by canvasId.
 *
 * When the AI "generate-chart" action embeds a canvas, the canvas entity is
 * created server-side and takes a beat to sync into the workspace projection.
 * Until it arrives the embed has no workspace row, which is indistinguishable
 * from "deleted" — so the embed needs an explicit `pending` signal.
 *
 * Kept as a plain module store (like {@link ./mermaid-view-store}) on purpose:
 * this is ephemeral UI state, NOT document data — it must never enter the CRDT
 * or the transaction history. The NodeView subscribes and resolves its render
 * state from the workspace row first, falling back to this store.
 */

const pending = new Set<string>();
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((fn) => fn());
}

/** True if the canvas is being generated and has not yet synced. */
export function isCanvasPending(canvasId: string): boolean {
  return pending.has(canvasId);
}

/** Mark the canvas as generating (shown while it syncs in). */
export function markCanvasPending(canvasId: string): void {
  pending.add(canvasId);
  notify();
}

/** Clear the signal — the canvas resolved (synced in) or the embed was removed. */
export function clearCanvasPending(canvasId: string): void {
  pending.delete(canvasId);
  notify();
}

/** Subscribe to store changes (the NodeView re-resolves its state). */
export function subscribeCanvasPending(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Test-only: clear all state between tests. */
export function _resetCanvasPendingStore(): void {
  pending.clear();
  listeners.clear();
}
